import { RATED_TALENT_CATEGORIES, type TalentCategory, type TalentProcessStatus, type TalentEligibilityStatus } from '../../../../../shared/types/talent-map';
import { getInitials as initialsOf } from '@/lib/avatar';
import { CURVE_ORDER, curveBucket, type TalentCurveKey } from '../../../../../shared/talent-map-stats';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Estados y categorías: UNA definición en shared/types/talent-map.ts. Se mantienen
// los nombres viejos (`ProcessStatus`, `EligibilityStatus`, `RATED_CATEGORIES`) por los 20+ importadores.
export type { TalentCategory } from '../../../../../shared/types/talent-map';
export type ProcessStatus = TalentProcessStatus;
export type EligibilityStatus = TalentEligibilityStatus;
export const RATED_CATEGORIES = RATED_TALENT_CATEGORIES;

export const ALL_CATEGORIES: TalentCategory[] = ['onboarding', ...RATED_CATEGORIES];

// Mockup palette (spec "Mapa de talento.docx"): Top=green, Sólida=YELLOW,
// Cumple=orange, Low=red, Onboarding=BLUE.
export const CATEGORY_CONFIG: Record<TalentCategory, {
  labelKey: string;
  shortKey: string;
  chip: string;        // pill badge classes
  chartColor: string;  // hex for recharts / legend dots
  bar: string;         // solid bar/accent class
  ring: string;        // selected-state ring classes
}> = {
  top_performer: {
    labelKey: 'performance.talent_map.category_top_performer',
    shortKey: 'performance.talent_map.category_short_top_performer',
    chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    chartColor: '#34D399',
    bar: 'bg-emerald-400',
    ring: 'border-emerald-400 ring-1 ring-emerald-200',
  },
  performance_solida: {
    labelKey: 'performance.talent_map.category_performance_solida',
    shortKey: 'performance.talent_map.category_short_performance_solida',
    chip: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300',
    chartColor: '#FBBF24',
    bar: 'bg-yellow-400',
    ring: 'border-yellow-400 ring-1 ring-yellow-200',
  },
  cumple_con_oportunidades: {
    labelKey: 'performance.talent_map.category_cumple_con_oportunidades',
    shortKey: 'performance.talent_map.category_short_cumple_con_oportunidades',
    chip: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
    chartColor: '#FB923C',
    bar: 'bg-orange-400',
    ring: 'border-orange-400 ring-1 ring-orange-200',
  },
  low_performance: {
    labelKey: 'performance.talent_map.category_low_performance',
    shortKey: 'performance.talent_map.category_short_low_performance',
    chip: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
    chartColor: '#F87171',
    bar: 'bg-red-400',
    ring: 'border-red-400 ring-1 ring-red-200',
  },
  onboarding: {
    labelKey: 'performance.talent_map.category_onboarding',
    shortKey: 'performance.talent_map.category_short_onboarding',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    chartColor: '#60A5FA',
    bar: 'bg-blue-400',
    ring: 'border-blue-400 ring-1 ring-blue-200',
  },
};

export const UNRATED_CHART_COLOR = '#D1D5DB';
/** Licencia (punto del eje desde el 21/09; mismo violeta del chip). */
export const LICENSE_CHART_COLOR = '#C4B5FD';
/** "Cumple con oportunidades · Sin confirmar": el naranja de Cumple, apagado. */
export const UNCONFIRMED_CHART_COLOR = '#FDBA74';

export const STATUS_CONFIG: Record<ProcessStatus, { dot: string }> = {
  not_started:      { dot: 'bg-gray-300' },
  draft:            { dot: 'bg-sky-400' },
  pending_evidence: { dot: 'bg-amber-400' },
  confirmed:        { dot: 'bg-emerald-500' },
  hrbp_modified:    { dot: 'bg-blue-500' },
  inactive:         { dot: 'bg-gray-200' },
  auto_assigned:    { dot: 'bg-violet-300' },
};

// Records created before the agent emitted a numeric confidence only have the enum
const LEGACY_CONFIDENCE_PERCENT: Record<'high' | 'medium' | 'low', number> = {
  high: 90, medium: 70, low: 45,
};

export function displayConfidencePercent(x?: {
  confidencePercent?: number | null;
  confidenceLevel?: 'high' | 'medium' | 'low' | null;
} | null): number | null {
  if (x?.confidencePercent != null) return x.confidencePercent;
  if (x?.confidenceLevel) return LEGACY_CONFIDENCE_PERCENT[x.confidenceLevel];
  return null;
}

/** Iniciales con '?' si no hay nombre (avatar del Mapa de Talento / D-Talk). Implementación: lib/avatar. */
export const getInitials = (name?: string, lastName?: string): string => initialsOf(name, lastName, '?');
export { getAvatarColor } from '@/lib/avatar';

/** Normalized row consumed by the Team tab + distribution panel, built from
 *  either getTeam (leader scope) or listAllForPeople (org scope). */
export interface TalentTeamRow {
  email: string;
  /** ID de nómina (columna A del Community Map). */
  employeeId?: number | null;
  name: string;
  lastName: string;
  impactLevel: string;
  role?: string | null;
  team?: string | null;
  area?: string | null;
  startDate?: string | null;
  country?: string | null;
  n1Manager?: string | null;
  clapbase?: string | null;
  leaderEmail?: string | null;
  /** "Nombre Apellido" del evaluador (scope recursivo). */
  leaderName?: string | null;
  /** Manager actual según el community map; el filtro "manager" usa este (reorg). */
  managerEmail?: string | null;
  /** false ⇒ reporte indirecto: solo lectura para el usuario actual. */
  isDirect?: boolean;
  /** pm o n1 del que mira: puede abrir la evaluación (aunque no sea directo real). */
  canEvaluate?: boolean;
  eligibilityStatus: string;
  processStatus: string;
  finalCategory: TalentCategory | null;
  confidencePercent: number | null;
  updatedAt: string | null;
  updatedBy?: string | null;
  hrbpModifiedBy?: string | null;
  hrbpModifiedAt?: string | null;
}

/** "ENERO – AGOSTO 2026" from the cycle config dates */
export function formatPeriodLabel(cfg?: { periodStart: string; periodEnd: string } | null): string {
  if (!cfg?.periodStart || !cfg?.periodEnd) return '';
  const start = new Date(`${cfg.periodStart}T00:00:00`);
  const end = new Date(`${cfg.periodEnd}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
  const month = (d: Date) => format(d, 'LLLL', { locale: es }).toUpperCase();
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  if (startYear !== endYear) return `${month(start)} ${startYear} – ${month(end)} ${endYear}`;
  return `${month(start)} – ${month(end)} ${endYear}`;
}

// ─── Curva de distribución: puntos ─────────────────────────────────────────

export interface CurvePoint {
  key: string;
  label: string;
  shortLabel: string;
  count: number;
  pct: number;
  color: string;
}

interface CurveRowLike {
  eligibilityStatus: string;
  processStatus: string;
  finalCategory: string | null;
}

/** Etiqueta, versión corta y color de cada punto (i18n + paleta del módulo). */
export function curvePointMeta(
  key: TalentCurveKey,
  t: (key: string, fallback?: string) => string,
): Pick<CurvePoint, 'label' | 'shortLabel' | 'color'> {
  switch (key) {
    case 'licencia': {
      const l = t('performance.talent_map.eligibility_license', 'Licencia');
      return { label: l, shortLabel: l, color: LICENSE_CHART_COLOR };
    }
    case 'cumple_sin_confirmar':
      return {
        label: t('performance.talent_map.category_cumple_sin_confirmar'),
        shortLabel: t('performance.talent_map.category_short_cumple_sin_confirmar'),
        color: UNCONFIRMED_CHART_COLOR,
      };
    default:
      return {
        label: t(CATEGORY_CONFIG[key].labelKey),
        shortLabel: t(CATEGORY_CONFIG[key].shortKey),
        color: CATEGORY_CONFIG[key].chartColor,
      };
  }
}

/**
 * Puntos de la curva a partir de filas (equipo o compañía). El bucket de cada
 * fila lo decide `curveBucket` (shared): una categoría cuenta solo confirmada,
 * lo que el cron dejó en auto_assigned va a "Cumple · Sin confirmar".
 */
export function buildCurvePoints(
  allRows: CurveRowLike[],
  t: (key: string, fallback?: string) => string,
): CurvePoint[] {
  // Onboarding y Licencia son puntos del eje (Sol 21/09): la curva es sobre todos.
  const rows = allRows;
  const total = rows.length;
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const counts = new Map<TalentCurveKey, number>();
  for (const r of rows) {
    const k = curveBucket(r);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return CURVE_ORDER.map(key => {
    const count = counts.get(key) ?? 0;
    return { key, count, pct: pct(count), ...curvePointMeta(key, t) };
  });
}
